import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';

import { ForceVerificationSetupGuard } from '@firestitch/2fa';
import { FsForcePasswordChangeGuard } from '@firestitch/password-reset';

import { LoggedInGuard, LoggedOutGuard } from '@common/guards';
import { routeMatcher } from '@common/helpers';

import { CardComponent } from '@libs/layouts/card';
import { SideNavComponent } from '@libs/layouts/side-nav';

const canActivateLoggedIn = [
  LoggedInGuard, 
  ForceVerificationSetupGuard, 
  FsForcePasswordChangeGuard,
];


export const routes: Routes = [
  {
    path: 'home',
    pathMatch: 'full',
    redirectTo: 'projects/tasks',
  },
  {
    matcher: routeMatcher,
    component: SideNavComponent,
    data: { fsNav: { root: true } },
    children: [
      {
        path: 'account',
        loadChildren: () => import('./account').then((m) => m.AccountModule),
        canActivate: canActivateLoggedIn,
      },
      {
        path: 'projects',
        loadChildren: () => import('./projects').then((m) => m.ProjectsModule),
        canActivate: canActivateLoggedIn,
      },
      {
        path: 'reports',
        loadChildren: () => import('./reports').then((m) => m.ReportsModule),
        canActivate: canActivateLoggedIn,
      },
      {
        path: 'workspace',
        loadChildren: () => import('./environment').then((m) => m.EnvironmentModule),
        canActivate: canActivateLoggedIn,
      },
      {
        path: 'admin',
        loadChildren: () => import('./admin').then((m) => m.AdminModule),
        canActivate: canActivateLoggedIn,
      },
      {
        path: 'system',
        loadChildren: () => import('./system').then((m) => m.SystemModule),
        canActivate: canActivateLoggedIn,
      },
      {
        path: 'notfound',
        loadChildren: () => import('@common/modules/not-found').then((m) => m.NotFoundModule),
      },
    ],
  },
  {
    matcher: routeMatcher,
    component: CardComponent,
    children: [
      {
        path: 'verification',
        loadChildren: () => import('@firestitch/2fa').then((m) => m.FsVerificationSetupModule),
      },
      {
        path: 'invite',
        loadChildren: () => import('@firestitch/invite').then((m) => m.FsInviteModule),
        canActivate: [LoggedOutGuard],
      },
      {
        path: 'signin',
        loadChildren: () => import('./signin').then((m) => m.SigninModule),
        canActivate: [LoggedOutGuard],
      },
      {
        path: 'signup',
        loadChildren: () => import('./signup').then((m) => m.SignupModule),
        canActivate: [LoggedOutGuard],
      },
      {
        path: 'offline',
        loadChildren: () => import('@common/modules/offline').then((m) => m.OfflineModule),
      },
      {
        path: 'password',
        children: [
          {
            path: 'change',
            loadChildren: () => import('@firestitch/password-reset').then((m) => m.FsForcePasswordChangeModule),
          },
        ],
      },
      {
        path: 'documentrequest',
        loadChildren: () => import('@firestitch/crm').then((m) => m.FsDocumentRequestModule),
      },
      {
        // The OAuth pages a person sees: the Allow page an app such as Claude sends
        // them to (signed in), and the error page a broken request lands on (open).
        // The guards are on the authorize child, so the error page is open.
        path: 'oauth',
        loadChildren: () => import('./oauth').then((m) => m.OAuthModule),
      },
    ],
  },
  {
    path: 'blank',
    component: CardComponent,
  },
  {
    // Share links (a plan share's public page). No guard: whoever has the
    // link may open it, signed in or not.
    path: 'share',
    loadChildren: () => import('./share').then((m) => m.ShareModule),
  },
  {
    path: '**',
    loadChildren: () => import('@common/modules/content').then((m) => m.ContentModule),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(
      routes,
      {
        preloadingStrategy: PreloadAllModules,
      },
    ),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
